import { useEffect, useState } from "react";
import { Pickaxe, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import api, { apiErr } from "@/lib/api";
import { Label, inputCls } from "./shared";
import { MiningXpCurveEditor } from "./MiningXpCurveEditor";

const ORE_RARITIES = ["common", "uncommon", "rare", "elite", "legendary"];

const EMPTY_CONFIG = {
  mines: [],
  smelting_recipes: [],
  tools: [],
  smithing_materials: [],
  discovery_rewards: [],
  projects: [],
  xp_curve_levels: [],
  default_xp_curve_levels: [],
  updated_at: null,
  updated_by: null,
};

const curveToImportText = (levels = []) => levels.map((entry) => entry.total_xp).join("\n");

const parseCurveImport = (value) => value
  .split(/[\s,]+/)
  .map((entry) => entry.trim())
  .filter(Boolean)
  .map((entry) => Number(entry));

export function MiningConfigPanel() {
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [xpCurveImport, setXpCurveImport] = useState("");
  const [savedXpCurveLevels, setSavedXpCurveLevels] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mining/admin/config");
      setConfig(data);
      setSavedXpCurveLevels((data.xp_curve_levels || []).map((entry) => ({ ...entry })));
      setXpCurveImport(curveToImportText(data.xp_curve_levels || []));
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patchMine = (mineId, field, value) => {
    setConfig((prev) => ({ ...prev, mines: prev.mines.map((mine) => mine.id === mineId ? { ...mine, [field]: value } : mine) }));
  };

  const patchMineRewardRange = (mineId, rarity, field, value) => {
    setConfig((prev) => ({
      ...prev,
      mines: prev.mines.map((mine) => mine.id === mineId ? {
        ...mine,
        raw_ore_table: (mine.raw_ore_table || []).map((entry) => entry.rarity === rarity ? { ...entry, [field]: value } : entry),
      } : mine),
    }));
  };

  const patchRecipe = (rarity, field, value) => {
    setConfig((prev) => ({ ...prev, smelting_recipes: prev.smelting_recipes.map((recipe) => recipe.rarity === rarity ? { ...recipe, [field]: value } : recipe) }));
  };

  const patchTool = (toolId, field, value) => {
    setConfig((prev) => ({ ...prev, tools: prev.tools.map((tool) => tool.id === toolId ? { ...tool, [field]: value } : tool) }));
  };

  const patchMaterialField = (materialId, field, value) => {
    setConfig((prev) => ({
      ...prev,
      smithing_materials: prev.smithing_materials.map((material) => material.id === materialId ? { ...material, [field]: value } : material),
    }));
  };

  const patchMaterialCost = (materialId, rarity, value) => {
    setConfig((prev) => ({
      ...prev,
      smithing_materials: prev.smithing_materials.map((material) => material.id === materialId ? { ...material, ore_costs: { ...material.ore_costs, [rarity]: value } } : material),
    }));
  };

  const patchProjectField = (projectId, field, value) => {
    setConfig((prev) => ({
      ...prev,
      projects: prev.projects.map((project) => project.id === projectId ? { ...project, [field]: value } : project),
    }));
  };

  const patchProjectCost = (projectId, rarity, value) => {
    setConfig((prev) => ({
      ...prev,
      projects: prev.projects.map((project) => project.id === projectId ? { ...project, ore_costs: { ...project.ore_costs, [rarity]: value } } : project),
    }));
  };

  const patchDiscoveryMilestone = (discoveryId, milestoneId, value) => {
    setConfig((prev) => ({
      ...prev,
      discovery_rewards: prev.discovery_rewards.map((discovery) => discovery.discovery_id === discoveryId ? {
        ...discovery,
        milestones: discovery.milestones.map((milestone) => milestone.milestone_id === milestoneId ? { ...milestone, required_count: value } : milestone),
      } : discovery),
    }));
  };

  const patchXpCurveLevel = (level, value) => {
    setConfig((prev) => ({
      ...prev,
      xp_curve_levels: prev.xp_curve_levels.map((entry) => entry.level === level ? { ...entry, total_xp: value } : entry),
    }));
  };

  const loadXpCurveFromImport = () => {
    const parsed = parseCurveImport(xpCurveImport);
    if (parsed.length !== 100 || parsed.some((value) => Number.isNaN(value))) {
      toast.error("Paste exactly 100 whole-number XP values for levels 1-100.");
      return;
    }
    setConfig((prev) => ({
      ...prev,
      xp_curve_levels: prev.xp_curve_levels.map((entry, index) => ({ ...entry, total_xp: parsed[index] })),
    }));
    toast.success("Imported XP curve into the editor.");
  };

  const resetXpCurveToCurrent = () => {
    setConfig((prev) => ({
      ...prev,
      xp_curve_levels: savedXpCurveLevels.map((entry) => ({ ...entry })),
    }));
    setXpCurveImport(curveToImportText(savedXpCurveLevels));
  };

  const loadDefaultXpCurve = () => {
    setConfig((prev) => ({
      ...prev,
      xp_curve_levels: (prev.default_xp_curve_levels || []).map((entry) => ({ ...entry })),
    }));
    setXpCurveImport(curveToImportText(config.default_xp_curve_levels || []));
    toast.success("Loaded the default Mining XP curve into the editor.");
  };

  const saveMine = async (mine) => {
    setSavingKey(`mine-${mine.id}`);
    try {
      await api.patch(`/mining/admin/config/mine/${mine.id}`, {
        required_level: Number(mine.required_level),
        rare_discovery_chance: Number(mine.rare_discovery_chance),
        deposit_capacity: Number(mine.deposit_capacity),
        deposit_cost_per_session: Number(mine.deposit_cost_per_session),
        deposit_restore_per_hour: Number(mine.deposit_restore_per_hour),
        depleted_duration_minutes: Number(mine.depleted_duration_minutes),
        raw_ore_table: Object.fromEntries((mine.raw_ore_table || []).map((entry) => [entry.rarity, {
          min: Number(entry.min || 0),
          max: Number(entry.max || 0),
        }])),
      });
      toast.success(`${mine.name} updated.`);
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSavingKey("");
    }
  };

  const saveRecipe = async (recipe) => {
    setSavingKey(`smelt-${recipe.rarity}`);
    try {
      await api.patch(`/mining/admin/config/smelting/${recipe.rarity}`, {
        ore_cost: Number(recipe.ore_cost),
        output_fragments: Number(recipe.output_fragments),
      });
      toast.success(`${recipe.rarity} smelting updated.`);
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSavingKey("");
    }
  };

  const saveTool = async (tool) => {
    setSavingKey(`tool-${tool.id}`);
    try {
      await api.patch(`/mining/admin/config/tool/${tool.id}`, {
        cost_gold: Number(tool.cost_gold),
        repair_gold_cost: Number(tool.repair_gold_cost),
      });
      toast.success(`${tool.name} costs updated.`);
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSavingKey("");
    }
  };

  const saveMaterial = async (material) => {
    setSavingKey(`material-${material.id}`);
    try {
      // Build payload with only relevant fields based on material category
      const payload = {
        craft_output: Number(material.craft_output),
        ore_costs: Object.fromEntries(ORE_RARITIES.map((rarity) => [rarity, Number(material.ore_costs?.[rarity] || 0)])),
      };
      // Only include effect fields for the appropriate category
      if (material.category === "tool_upgrade") {
        payload.max_durability_bonus = Number(material.max_durability_bonus || 0);
      }
      if (material.category === "forge_booster") {
        payload.floor_bonus = Number(material.floor_bonus || 0);
      }
      if (material.category === "mining_boost") {
        payload.discovery_bonus = Number(material.discovery_bonus || 0);
      }
      await api.patch(`/mining/admin/config/material/${material.id}`, payload);
      toast.success(`${material.name} settings updated.`);
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSavingKey("");
    }
  };

  const saveDiscoveryMilestone = async (discoveryId, milestone) => {
    setSavingKey(`discovery-${milestone.milestone_id}`);
    try {
      await api.patch(`/mining/admin/config/discovery-milestone/${discoveryId}/${milestone.milestone_id}`, {
        required_count: Number(milestone.required_count),
      });
      toast.success(`${milestone.label} updated.`);
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSavingKey("");
    }
  };

  const saveProject = async (project) => {
    setSavingKey(`project-${project.id}`);
    try {
      await api.patch(`/mining/admin/config/project/${project.id}`, {
        duration_minutes: Number(project.duration_minutes),
        ore_costs: Object.fromEntries(ORE_RARITIES.map((rarity) => [rarity, Number(project.ore_costs?.[rarity] || 0)])),
        deposit_capacity_bonus: Number(project.deposit_capacity_bonus || 0),
        deposit_restore_per_hour_bonus: Number(project.deposit_restore_per_hour_bonus || 0),
        raw_ore_yield_bonus: Number(project.raw_ore_yield_bonus || 0),
        great_yield_chance_bonus: Number(project.great_yield_chance_bonus || 0),
        discovery_bonus: Number(project.discovery_bonus || 0),
      });
      toast.success(`${project.name} updated.`);
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSavingKey("");
    }
  };

  const saveXpCurve = async () => {
    setSavingKey("xp-curve");
    try {
      const xpTable = (config.xp_curve_levels || []).map((entry) => Number(entry.total_xp));
      if (xpTable.length !== 100 || xpTable.some((value) => !Number.isInteger(value))) {
        throw new Error("The XP curve must include 100 whole-number level values.");
      }
      await api.patch("/mining/admin/config/xp-curve", { xp_table: xpTable });
      toast.success("Mining XP curve updated.");
      await load();
    } catch (error) {
      toast.error(error?.message || apiErr(error));
    } finally {
      setSavingKey("");
    }
  };

  return (
    <section data-testid="admin-mining-config-panel" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4 border border-amber-700/40 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-4 rounded-sm">
        <div>
          <div className="mb-2 flex items-center gap-2 text-amber-300"><Pickaxe size={16} /><span className="text-xs uppercase tracking-[0.2em]">Administration</span></div>
          <h2 className="font-cinzel text-2xl text-amber-300">Mining Controls</h2>
          <p className="text-sm text-zinc-500 mt-2">Tune mine pressure, smithing materials, long-term projects, and pickaxe economy from one place.</p>
          {config.updated_at && <div data-testid="admin-mining-config-meta" className="mt-3 text-xs uppercase tracking-[0.18em] text-zinc-500">Last updated {new Date(config.updated_at).toLocaleString()} {config.updated_by ? `• ${config.updated_by}` : ""}</div>}
        </div>
        <button data-testid="admin-mining-config-refresh" onClick={load} disabled={loading} className="inline-flex items-center gap-2 border border-zinc-700 bg-zinc-900 px-4 py-2 text-sm text-zinc-200 transition-colors hover:bg-zinc-800 disabled:opacity-50">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </header>

      {loading ? <div data-testid="admin-mining-config-loading" className="text-sm text-zinc-500">Loading mining config…</div> : (
        <div className="space-y-8">
          <MiningXpCurveEditor
            curveLevels={config.xp_curve_levels || []}
            importValue={xpCurveImport}
            onImportChange={setXpCurveImport}
            onLoadImport={loadXpCurveFromImport}
            onPatchLevel={patchXpCurveLevel}
            onResetCurrent={resetXpCurveToCurrent}
            onLoadDefault={loadDefaultXpCurve}
            onApply={saveXpCurve}
            saving={savingKey === "xp-curve"}
          />

          <section>
            <h3 className="font-cinzel uppercase tracking-widest text-sm text-amber-300 mb-3">Mine Deposits & Discoveries</h3>
            <div className="grid xl:grid-cols-2 gap-4">
              {config.mines.map((mine) => (
                <article key={mine.id} data-testid={`admin-mining-mine-${mine.id}`} className="border border-zinc-800 bg-zinc-950/60 p-4 rounded-sm">
                  <div className="font-cinzel text-zinc-100">{mine.name}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">Level {mine.required_level} unlock</div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    <div><Label>Required level</Label><input data-testid={`admin-mine-required-level-${mine.id}`} type="number" value={mine.required_level} onChange={(e) => patchMine(mine.id, "required_level", e.target.value)} className={inputCls} /></div>
                    <div><Label>Discovery chance</Label><input data-testid={`admin-mine-discovery-${mine.id}`} type="number" step="0.001" value={mine.rare_discovery_chance} onChange={(e) => patchMine(mine.id, "rare_discovery_chance", e.target.value)} className={inputCls} /></div>
                    <div><Label>Deposit capacity</Label><input data-testid={`admin-mine-capacity-${mine.id}`} type="number" value={mine.deposit_capacity} onChange={(e) => patchMine(mine.id, "deposit_capacity", e.target.value)} className={inputCls} /></div>
                    <div><Label>Units per session</Label><input data-testid={`admin-mine-cost-${mine.id}`} type="number" value={mine.deposit_cost_per_session} onChange={(e) => patchMine(mine.id, "deposit_cost_per_session", e.target.value)} className={inputCls} /></div>
                    <div><Label>Restore per hour</Label><input data-testid={`admin-mine-restore-${mine.id}`} type="number" value={mine.deposit_restore_per_hour} onChange={(e) => patchMine(mine.id, "deposit_restore_per_hour", e.target.value)} className={inputCls} /></div>
                    <div><Label>Exhausted cooldown minutes</Label><input data-testid={`admin-mine-cooldown-${mine.id}`} type="number" value={mine.depleted_duration_minutes} onChange={(e) => patchMine(mine.id, "depleted_duration_minutes", e.target.value)} className={inputCls} /></div>
                  </div>
                  <div className="mt-4 space-y-3">
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Expected reward ranges</div>
                    <div className="grid gap-3">
                      {(mine.raw_ore_table || []).map((entry) => (
                        <div key={`${mine.id}-${entry.rarity}`} className="grid grid-cols-[minmax(0,1fr)_110px_110px] gap-3 items-end">
                          <div className="text-sm text-zinc-300 capitalize">{entry.rarity}</div>
                          <div>
                            <Label>Min</Label>
                            <input data-testid={`admin-mine-reward-min-${mine.id}-${entry.rarity}`} type="number" value={entry.min} onChange={(e) => patchMineRewardRange(mine.id, entry.rarity, "min", e.target.value)} className={inputCls} />
                          </div>
                          <div>
                            <Label>Max</Label>
                            <input data-testid={`admin-mine-reward-max-${mine.id}-${entry.rarity}`} type="number" value={entry.max} onChange={(e) => patchMineRewardRange(mine.id, entry.rarity, "max", e.target.value)} className={inputCls} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button data-testid={`admin-mine-save-${mine.id}`} onClick={() => saveMine(mine)} disabled={savingKey === `mine-${mine.id}`} className="mt-4 w-full px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-50">{savingKey === `mine-${mine.id}` ? "Saving…" : "Save Mine Settings"}</button>
                </article>
              ))}
            </div>
          </section>

          <section>
            <h3 className="font-cinzel uppercase tracking-widest text-sm text-amber-300 mb-3">Smelting Recipes</h3>
            <div className="grid xl:grid-cols-2 gap-4">
              {config.smelting_recipes.map((recipe) => (
                <article key={recipe.rarity} data-testid={`admin-smelting-${recipe.rarity}`} className="border border-zinc-800 bg-zinc-950/60 p-4 rounded-sm">
                  <div className="font-cinzel text-zinc-100 capitalize mb-3">{recipe.rarity}</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label>Ore cost</Label><input data-testid={`admin-smelting-ore-${recipe.rarity}`} type="number" value={recipe.ore_cost} onChange={(e) => patchRecipe(recipe.rarity, "ore_cost", e.target.value)} className={inputCls} /></div>
                    <div><Label>Fragment output</Label><input data-testid={`admin-smelting-output-${recipe.rarity}`} type="number" value={recipe.output_fragments} onChange={(e) => patchRecipe(recipe.rarity, "output_fragments", e.target.value)} className={inputCls} /></div>
                  </div>
                  <button data-testid={`admin-smelting-save-${recipe.rarity}`} onClick={() => saveRecipe(recipe)} disabled={savingKey === `smelt-${recipe.rarity}`} className="mt-4 w-full px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm border border-amber-600 text-amber-300 hover:bg-amber-900/30 disabled:opacity-50">{savingKey === `smelt-${recipe.rarity}` ? "Saving…" : "Save Smelting Recipe"}</button>
                </article>
              ))}
            </div>
          </section>

          <section>
            <h3 className="font-cinzel uppercase tracking-widest text-sm text-amber-300 mb-3">Pickaxe Buy & Repair Costs</h3>
            <div className="grid xl:grid-cols-2 gap-4">
              {config.tools.map((tool) => (
                <article key={tool.id} data-testid={`admin-mining-tool-${tool.id}`} className="border border-zinc-800 bg-zinc-950/60 p-4 rounded-sm">
                  <div className="font-cinzel text-zinc-100">{tool.name}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">Level {tool.required_level} unlock</div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    <div><Label>Buy cost (gold)</Label><input data-testid={`admin-tool-buy-cost-${tool.id}`} type="number" value={tool.cost_gold} onChange={(e) => patchTool(tool.id, "cost_gold", e.target.value)} className={inputCls} /></div>
                    <div><Label>Base repair cost (gold)</Label><input data-testid={`admin-tool-repair-cost-${tool.id}`} type="number" value={tool.repair_gold_cost} onChange={(e) => patchTool(tool.id, "repair_gold_cost", e.target.value)} className={inputCls} /></div>
                  </div>
                  <button data-testid={`admin-tool-save-${tool.id}`} onClick={() => saveTool(tool)} disabled={savingKey === `tool-${tool.id}`} className="mt-4 w-full px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm border border-amber-600 text-amber-300 hover:bg-amber-900/30 disabled:opacity-50">{savingKey === `tool-${tool.id}` ? "Saving…" : "Save Pickaxe Costs"}</button>
                </article>
              ))}
            </div>
          </section>

          <section>
            <h3 className="font-cinzel uppercase tracking-widest text-sm text-amber-300 mb-3">Phase 3 Smithing Material Costs</h3>
            <div className="grid xl:grid-cols-2 gap-4">
              {config.smithing_materials.map((material) => (
                <article key={material.id} data-testid={`admin-smithing-material-${material.id}`} className="border border-zinc-800 bg-zinc-950/60 p-4 rounded-sm">
                  <div className="font-cinzel text-zinc-100">{material.name}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">{material.category.replaceAll("_", " ")}</div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    {ORE_RARITIES.map((rarity) => (
                      <div key={rarity}>
                        <Label>{rarity} ore cost</Label>
                        <input data-testid={`admin-material-cost-${material.id}-${rarity}`} type="number" value={material.ore_costs?.[rarity] ?? 0} onChange={(e) => patchMaterialCost(material.id, rarity, e.target.value)} className={inputCls} />
                      </div>
                    ))}
                    <div>
                      <Label>Craft output</Label>
                      <input data-testid={`admin-material-output-${material.id}`} type="number" value={material.craft_output} onChange={(e) => patchMaterialField(material.id, "craft_output", e.target.value)} className={inputCls} />
                    </div>
                    {material.category === "tool_upgrade" && (
                      <div>
                        <Label>Max durability bonus</Label>
                        <input data-testid={`admin-material-durability-bonus-${material.id}`} type="number" value={material.max_durability_bonus || 0} onChange={(e) => patchMaterialField(material.id, "max_durability_bonus", e.target.value)} className={inputCls} />
                      </div>
                    )}
                    {material.category === "forge_booster" && (
                      <div>
                        <Label>Minimum stat roll quality bonus</Label>
                        <input data-testid={`admin-material-floor-bonus-${material.id}`} type="number" step="0.01" value={material.floor_bonus || 0} onChange={(e) => patchMaterialField(material.id, "floor_bonus", e.target.value)} className={inputCls} />
                      </div>
                    )}
                    {material.category === "mining_boost" && (
                      <div>
                        <Label>Discovery chance bonus</Label>
                        <input data-testid={`admin-material-discovery-bonus-${material.id}`} type="number" step="0.01" value={material.discovery_bonus || 0} onChange={(e) => patchMaterialField(material.id, "discovery_bonus", e.target.value)} className={inputCls} />
                      </div>
                    )}
                  </div>
                  <button data-testid={`admin-material-save-${material.id}`} onClick={() => saveMaterial(material)} disabled={savingKey === `material-${material.id}`} className="mt-4 w-full px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm border border-amber-600 text-amber-300 hover:bg-amber-900/30 disabled:opacity-50">{savingKey === `material-${material.id}` ? "Saving…" : "Save Smithing Material Settings"}</button>
                </article>
              ))}
            </div>
          </section>

          <section>
            <h3 className="font-cinzel uppercase tracking-widest text-sm text-amber-300 mb-3">Discovery Reward Milestone Requirements</h3>
            <div className="grid xl:grid-cols-2 gap-4">
              {config.discovery_rewards.map((discovery) => (
                <article key={discovery.discovery_id} data-testid={`admin-discovery-reward-${discovery.discovery_id}`} className="border border-zinc-800 bg-zinc-950/60 p-4 rounded-sm space-y-4">
                  <div>
                    <div className="font-cinzel text-zinc-100">{discovery.discovery_name}</div>
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">{discovery.rarity} discovery</div>
                  </div>
                  <div className="space-y-3">
                    {discovery.milestones.map((milestone) => (
                      <div key={milestone.milestone_id} className="border border-zinc-800 bg-zinc-900/50 p-3 rounded-sm space-y-3">
                        <div>
                          <div className="text-sm text-zinc-100">{milestone.label}</div>
                          <div className="text-xs text-zinc-500 mt-1">{milestone.reward_summary}</div>
                          {milestone.unlock_project_name && <div className="text-xs text-amber-300 mt-1">Unlocks project: {milestone.unlock_project_name}</div>}
                          {milestone.unlock_material_name && <div className="text-xs text-amber-300 mt-1">Unlocks material: {milestone.unlock_material_name}</div>}
                        </div>
                        <div className="grid grid-cols-[minmax(0,1fr)_180px] gap-3 items-end">
                          <div>
                            <Label>Required discoveries</Label>
                            <input data-testid={`admin-discovery-required-${discovery.discovery_id}-${milestone.milestone_id}`} type="number" value={milestone.required_count} onChange={(e) => patchDiscoveryMilestone(discovery.discovery_id, milestone.milestone_id, e.target.value)} className={inputCls} />
                          </div>
                          <button data-testid={`admin-discovery-save-${discovery.discovery_id}-${milestone.milestone_id}`} onClick={() => saveDiscoveryMilestone(discovery.discovery_id, milestone)} disabled={savingKey === `discovery-${milestone.milestone_id}`} className="px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm border border-amber-600 text-amber-300 hover:bg-amber-900/30 disabled:opacity-50">{savingKey === `discovery-${milestone.milestone_id}` ? "Saving…" : "Save Threshold"}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section>
            <h3 className="font-cinzel uppercase tracking-widest text-sm text-amber-300 mb-3">Phase 3 Pass 2 Projects</h3>
            <div className="grid xl:grid-cols-2 gap-4">
              {config.projects.map((project) => (
                <article key={project.id} data-testid={`admin-mining-project-${project.id}`} className="border border-zinc-800 bg-zinc-950/60 p-4 rounded-sm">
                  <div className="font-cinzel text-zinc-100">{project.name}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">{project.category.replaceAll("_", " ")} · unlocks from {project.unlock_discovery_name} milestone {project.unlock_milestone}</div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    {ORE_RARITIES.map((rarity) => (
                      <div key={rarity}>
                        <Label>{rarity} ore cost</Label>
                        <input data-testid={`admin-project-cost-${project.id}-${rarity}`} type="number" value={project.ore_costs?.[rarity] ?? 0} onChange={(e) => patchProjectCost(project.id, rarity, e.target.value)} className={inputCls} />
                      </div>
                    ))}
                    <div>
                      <Label>Duration minutes</Label>
                      <input data-testid={`admin-project-duration-${project.id}`} type="number" value={project.duration_minutes} onChange={(e) => patchProjectField(project.id, "duration_minutes", e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <Label>Deposit capacity bonus</Label>
                      <input data-testid={`admin-project-capacity-bonus-${project.id}`} type="number" value={project.deposit_capacity_bonus || 0} onChange={(e) => patchProjectField(project.id, "deposit_capacity_bonus", e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <Label>Deposit recovery / hour bonus</Label>
                      <input data-testid={`admin-project-recovery-bonus-${project.id}`} type="number" value={project.deposit_restore_per_hour_bonus || 0} onChange={(e) => patchProjectField(project.id, "deposit_restore_per_hour_bonus", e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <Label>Ore yield bonus</Label>
                      <input data-testid={`admin-project-yield-bonus-${project.id}`} type="number" step="0.01" value={project.raw_ore_yield_bonus || 0} onChange={(e) => patchProjectField(project.id, "raw_ore_yield_bonus", e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <Label>Great yield chance bonus</Label>
                      <input data-testid={`admin-project-great-yield-bonus-${project.id}`} type="number" step="0.01" value={project.great_yield_chance_bonus || 0} onChange={(e) => patchProjectField(project.id, "great_yield_chance_bonus", e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <Label>Discovery chance bonus</Label>
                      <input data-testid={`admin-project-discovery-bonus-${project.id}`} type="number" step="0.01" value={project.discovery_bonus || 0} onChange={(e) => patchProjectField(project.id, "discovery_bonus", e.target.value)} className={inputCls} />
                    </div>
                  </div>
                  <button data-testid={`admin-project-save-${project.id}`} onClick={() => saveProject(project)} disabled={savingKey === `project-${project.id}`} className="mt-4 w-full px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm border border-amber-600 text-amber-300 hover:bg-amber-900/30 disabled:opacity-50">{savingKey === `project-${project.id}` ? "Saving…" : "Save Project Settings"}</button>
                </article>
              ))}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}